// hooks/useNodeValuesSync.ts
import { useEffect, useRef } from 'react';
import type React from 'react';
import type { InputNode, NodeValue } from '../types';

// values state 只存 string | string[]。
// 例外：upload 节点的值（图片/文件 URL 字符串或 File）归 files state 管 —— UploadNode/
// UploadChip 只读 files（且 UploadChip 支持 string URL 当预览），所以回填来的 upload 值
// 必须路由进 files，否则进了 values 没有任何组件消费 → 不渲染（历史 bug：图片回填不显示）。
const isStringValue = (v: NodeValue): v is string | string[] =>
  typeof v === 'string' ||
  (Array.isArray(v) && v.every((x) => typeof x === 'string'));

// 比较两个 string | string[] 值是否相等（用于判定 external 是否真的变化）。
const sameStringValue = (
  a: string | string[] | undefined,
  b: string | string[] | undefined,
): boolean => {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((x, i) => x === b[i]);
  }
  return false;
};

// 收集 config 里 upload 节点 key → 是否批量模式。
// 批量判定必须与 UploadNode 的 isBatch 完全一致（maxCount>1 且 minCount 是 number），
// 否则：拆了 `${key}-${idx}` slot 但 UploadNode 走单文件模式读 `files[key]` → 读不到 → 丢图。
const collectUploadMeta = (config: InputNode[]): Map<string, { batch: boolean; maxCount: number }> => {
  const meta = new Map<string, { batch: boolean; maxCount: number }>();
  for (const node of config) {
    if (node.type === 'upload' && node.key) {
      const batch =
        typeof node.maxCount === 'number' &&
        typeof node.minCount === 'number' &&
        node.maxCount > 1;
      meta.set(node.key, { batch, maxCount: batch ? node.maxCount! : 1 });
    }
  }
  return meta;
};

export function useNodeValuesSync(
  external: Record<string, NodeValue> | undefined,
  values: Record<string, string | string[]>,
  setValues: React.Dispatch<React.SetStateAction<Record<string, string | string[]>>>,
  config: InputNode[],
  files: Record<string, File | string | null>,
  setFiles: React.Dispatch<React.SetStateAction<Record<string, File | string | null>>>,
) {
  // 记住上一次处理过的 external 各 key 的值。用于区分两种回填：
  //  - external[k] 相比上次"变了" → 是一次新的回填（如发送后拉到最新提交参数）→ 覆盖内部值，
  //    满足"点发送后所有字段刷成最新提交值"，且跨智能体/连续追加都生效。
  //  - external[k] 没变（轮询重复喂同样的值）→ 维持原"只填空"语义，不动用户已编辑的内容。
  // 注意：用户手动编辑只改内部 values、不会改 external prop，所以永远不会被这里覆盖。
  const prevExternalRef = useRef<Record<string, string | string[]>>({});

  useEffect(() => {
    if (!external) return;
    const uploadMeta = collectUploadMeta(config);

    // ── 1) 非 upload 的字符串值 → values ──
    setValues((prev) => {
      const next: Record<string, string | string[]> = { ...prev };
      let changed = false;
      for (const [k, v] of Object.entries(external)) {
        if (uploadMeta.has(k)) continue; // upload 走 files 分支
        if (!isStringValue(v)) continue;
        const cur = next[k];
        const isEmpty =
          cur === undefined ||
          cur === '' ||
          (Array.isArray(cur) && cur.length === 0);
        // external 相比上次变化 = 新回填 → 覆盖；否则仅填空。
        const externalChanged = !sameStringValue(prevExternalRef.current[k], v);
        if (isEmpty || externalChanged) {
          if (!sameStringValue(cur, v)) {
            next[k] = v;
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });

    // ── 2) upload 节点的值（string | string[] | File | File[]）→ files ──
    setFiles((prev) => {
      const next: Record<string, File | string | null> = { ...prev };
      let changed = false;
      for (const [k, meta] of uploadMeta) {
        const v = external[k];
        if (v === undefined || v === null) continue;
        const list = (Array.isArray(v) ? v : [v]).filter(
          (x) => x != null && x !== '',
        ) as Array<string | File>;
        if (list.length === 0) continue;

        if (meta.batch) {
          // 批量：逐 slot 写 `${key}-${idx}`，仅在该 slot 为空时回填（不覆盖用户已选）
          for (let i = 0; i < list.length && i < meta.maxCount; i += 1) {
            const slotKey = `${k}-${i}`;
            if (next[slotKey] == null) {
              next[slotKey] = list[i];
              changed = true;
            }
          }
        } else {
          // 单文件：仅在该 key 为空时回填，取首个值（非批量节点 UploadNode 只读 files[key]）
          if (next[k] == null) {
            next[k] = list[0];
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });

    // 记录本次 external 的字符串值快照，供下次判定"是否变化"。upload 类不参与文本覆盖逻辑。
    const snapshot: Record<string, string | string[]> = {};
    for (const [k, v] of Object.entries(external)) {
      if (isStringValue(v)) snapshot[k] = v;
    }
    prevExternalRef.current = snapshot;
    // files 故意不入依赖：避免回填后 files 变化触发 effect 重跑造成回环；
    // 回填仅在 external/config 变化时执行，"仅填空 slot" 语义保证幂等。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [external, config, setValues, setFiles]);
}
